import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, GraduationCap, ExternalLink } from "lucide-react";

export default function AcademicPharmacy() {
  return (
    <PharmacistLayout title="Academic Pharmacy">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-amber-700 to-amber-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">Academic Pharmacy Pathways</h2>
              <p className="text-amber-300 text-sm">PhD, Clinical Academic, NIHR, and Research Careers</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Academic pharmacy combines clinical practice with research, education, and scholarship. For rheumatology pharmacists, an academic pathway opens doors to leading national research programmes, developing evidence-based practice, and influencing medicines policy.
          </p>
        </div>

        {[
          {
            title: "PhD Pathway",
            content: [
              { heading: "When to pursue", text: "A PhD is typically pursued after 2–4 years of clinical practice, once you have a clear research question and an academic supervisor. Some pharmacists complete a PhD part-time alongside NHS practice." },
              { heading: "Finding a project", text: "Look for PhD studentships funded by NIHR, ESPRC, pharmaceutical companies (industry PhDs), or universities. Rheumatology topics include pharmacovigilance, medicines adherence, biosimilar switching, and real-world effectiveness of biologics." },
              { heading: "Funding", text: "NIHR PhD Fellowships fund full-time doctoral study for healthcare professionals. The NIHR Academy provides additional research training support. Some NHS trusts fund part-time PhD study as part of consultant pharmacist development." },
              { heading: "Duration", text: "Full-time PhD: 3–4 years. Part-time: 5–7 years." },
            ],
          },
          {
            title: "Clinical Academic Roles",
            content: [
              { heading: "What is a clinical academic?", text: "A clinical academic divides their time between NHS clinical practice and academic research/teaching, typically 50:50 or 60:40. They hold dual appointments at an NHS trust and a university." },
              { heading: "NIHR Clinical Lectureships & Senior Lectureships", text: "Competitively awarded NIHR fellowships that fund 50% academic time. Clinical Lectureships are post-PhD; Senior Lectureships are for established researchers. These are the main funded routes into clinical academic pharmacy." },
              { heading: "Academic Clinical Fellowship (ACF)", text: "The ACF scheme (primarily used in medicine but open to allied health) provides 25% protected research time during clinical training. Pharmacists may access equivalent schemes through NIHR." },
            ],
          },
          {
            title: "NIHR Funding Landscape",
            content: [
              { heading: "NIHR Academy", text: "The NIHR Academy coordinates research training awards. Relevant schemes: Pre-doctoral Fellowships (PDFs), NIHR Doctoral Fellowships, Clinical Lectureships." },
              { heading: "Research for Patient Benefit (RfPB)", text: "NIHR programme for applied health research. Pharmacists can lead or co-lead bids. Rheumatology pharmacy research areas: shared care monitoring, biosimilar adherence, NMP clinics, patient experience." },
              { heading: "Health Technology Assessment (HTA)", text: "NIHR HTA funds research to assess medicines and health technologies. Pharmacist involvement in HTA studies (pharmacoeconomics, modelling) is increasingly valued." },
            ],
          },
          {
            title: "University Lectureship",
            content: [
              { heading: "Route", text: "Apply for Lecturer / Senior Lecturer positions at pharmacy schools. Usually requires a PhD and evidence of research and teaching. Can be combined with honorary NHS clinical sessions." },
              { heading: "Teaching focus", text: "Rheumatology pharmacists with clinical expertise are well placed to teach clinical pharmacology, therapeutics, and pharmacy practice to MPharm students." },
              { heading: "Reader / Professor", text: "Progression through academic ranks (Lecturer → Senior Lecturer → Reader → Professor) is research-output led — grants, publications, PhD supervision, and international reputation." },
            ],
          },
        ].map((section) => (
          <div key={section.title} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-bold text-slate-900 mb-4">{section.title}</h3>
            <div className="space-y-4">
              {section.content.map((item, i) => (
                <div key={i}>
                  <p className="text-sm font-semibold text-slate-800 mb-1">{item.heading}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-3">Useful Links</h4>
          <div className="space-y-2">
            {[
              { label: "NIHR Academy", url: "https://www.nihr.ac.uk/explore-nihr/academy-programmes/" },
              { label: "RPS Research & Evidence resources", url: "https://www.rpharms.com/resources/research" },
              { label: "UK Clinical Research Collaboration (UKCRC)", url: "https://www.ukcrc.org/" },
              { label: "Pharmacy Research UK", url: "https://www.pharmacyresearchuk.org/" },
            ].map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800 transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </PharmacistLayout>
  );
}
